/**
 * What a candidate (and the employer, for the same application) is shown for a match on a job: one rule
 * for the job board card, the job page, its phone apply bar and the employer's applicant view, so they
 * never disagree.
 *
 * THE NUMBER IS ALWAYS SHOWN (2026-10-13 logic audit). Since Oct 10 the EMT's v3 job cards showed the
 * score next to "What's missing" while the employer's view and the older cards replaced the same
 * number with "Not eligible" or "Match after document review", so the two sides did not show the same
 * thing. Now everyone sees the score, plus the reason when a required item is not met. Under the
 * counting rule a required credential that is not on the profile, not reviewed yet or expired is not
 * met, so the score is capped (calculateJobMatch MISSING_REQUIRED_CERT_MAX_SCORE) and the words say why.
 */
export type CandidateMatchLabel = {
  text: string;
  /** Kept for older readers: the number is never withheld any more, so this is always false. */
  pending: boolean;
  /** Longer explanation for a tooltip / accessible description. */
  explanation: string;
};

/** Older wording, kept for readers that still import it. */
export const MATCH_PENDING_DOCUMENTS_TEXT = 'Match after document review';
export const MATCH_NOT_ELIGIBLE_TEXT = 'Not eligible';

export type CandidateMatchGate = {
  /** The "Documents Reviewed" apply gate is unmet (a required credential is pending, rejected or expired). */
  documentsBlocked: boolean;
  /** A certification the employer marked REQUIRED is not on the profile at all. */
  hardGateFailed?: boolean;
  /** Which required certifications are missing, for the explanation. */
  missingRequired?: string[];
  /** Required certifications on the profile whose document is not reviewed yet, rejected or expired. */
  notReviewed?: string[];
};

function list(names: readonly string[]): string {
  const n = names.filter(Boolean);
  if (n.length <= 1) return n[0] ?? '';
  return `${n.slice(0, -1).join(', ')} and ${n[n.length - 1]}`;
}

export function candidateMatchLabel(
  score: number | null,
  gate: boolean | CandidateMatchGate,
): CandidateMatchLabel | null {
  if (score == null) return null;
  const g: CandidateMatchGate = typeof gate === 'boolean' ? { documentsBlocked: gate } : gate;
  const missing = (g.missingRequired || []).filter(Boolean);
  const notReviewed = (g.notReviewed || []).filter(Boolean);
  if (g.hardGateFailed || missing.length) {
    return {
      text: missing.length ? `${score}% match, missing ${list(missing)}` : `${score}% match, a required credential is missing`,
      pending: false,
      explanation: missing.length
        ? `This job requires ${list(missing)}, which is not on the profile. Until it is added and its document is reviewed, the score is capped at 35.`
        : 'A credential this job requires is not on the profile.',
    };
  }
  if (g.documentsBlocked) {
    return {
      text: notReviewed.length ? `${score}% match, ${list(notReviewed)} not counted yet` : `${score}% match, documents not reviewed yet`,
      pending: false,
      explanation:
        'A required credential counts once its document passes our document review and while it is current. Until then the score is capped at 35.',
    };
  }
  return {
    text: `${score}% match`,
    pending: false,
    explanation: 'How well the profile meets what this employer asked for.',
  };
}
